import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute, AdminRoute, AdminOrProjectPMRoute, AdminManagerOrPMRoute, AdminOrPMRoute, ManagerRoute, TaskManagerRoute, SalesRoute, LeadsBoardRoute, AdminFinanceManagerOrPMRoute, HRRoute, FinanceRoute, FinanceOnlyRoute, ISMSRoute, SuperadminRoute } from './components/auth/ProtectedRoute';
import { ProjectEditRoute } from './components/auth/ProjectEngineeringEditRoute';
import { RequireFlag } from './components/auth/RequireFlag';
import { useDateFormatSync } from './hooks/useDateFormatSync';
import { useReportingFxSync } from './hooks/useReportingFxSync';
import { useCtrlEnterSubmit } from './hooks/useCtrlEnterSubmit';
import { RequireModule } from './components/auth/RequireModule';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/auth/LoginPage';
import { CallbackPage } from './pages/auth/CallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { MyTrainingsPage } from './pages/trainings/MyTrainingsPage';
import { TrainingAdminPage } from './pages/admin/TrainingAdminPage';
import { HelpdeskPage } from './pages/HelpdeskPage';
import { DocsPage } from './pages/docs/DocsPage';
import { TimeEntryPage } from './pages/time/TimeEntryPage';
import { PendingTimesheetsPage } from './pages/manager/PendingTimesheetsPage';
import { TaskManagementPage } from './pages/manager/TaskManagementPage';
import { PendingExpensesPage } from './pages/manager/PendingExpensesPage';
import { PendingTimeOffsPage } from './pages/manager/PendingTimeOffsPage';
import { PendingJoinRequestsPage } from './pages/manager/PendingJoinRequestsPage';
import { PendingBackdatedRequestsPage } from './pages/manager/PendingBackdatedRequestsPage';
import { TimesheetDetailPage as ManagerTimesheetDetailPage } from './pages/manager/TimesheetDetailPage';
import ProjectListPage from './pages/projects/ProjectListPage';
import ProjectDetailPage from './pages/projects/ProjectDetailPage';
import ProjectFormPage from './pages/projects/ProjectFormPage';
import TimeOffListPage from './pages/time-off/TimeOffListPage';
import TimeOffDetailPage from './pages/time-off/TimeOffDetailPage';
import ExpenseListPage from './pages/expenses/ExpenseListPage';
import ExpenseDetailPage from './pages/expenses/ExpenseDetailPage';
import EmployeeInvoiceListPage from './pages/EmployeeInvoiceListPage';
import EmployeeInvoiceDetailPage from './pages/EmployeeInvoiceDetailPage';
import InvoiceListPage from './pages/invoices/InvoiceListPage';
import InvoiceDetailPage from './pages/invoices/InvoiceDetailPage';
import InvoiceGeneratePage from './pages/invoices/InvoiceGeneratePage';
import InvoicePendingPage from './pages/invoices/InvoicePendingPage';
import ReceivablesPage from './pages/invoices/ReceivablesPage';
import InvoiceImportPage from './pages/invoices/InvoiceImportPage';
import ReportsPage from './pages/reports/ReportsPage';
import ProjectReportPage from './pages/reports/ProjectReportPage';
import AttendanceReportPage from './pages/reports/AttendanceReportPage';
import PayrollReportPage from './pages/reports/PayrollReportPage';
import BillabilityReportPage from './pages/reports/BillabilityReportPage';
import RevenueKpiPage from './pages/reports/RevenueKpiPage';
import ProjectDetailedReportPage from './pages/reports/ProjectDetailedReportPage';
import MoneyGlossaryPage from './pages/reports/MoneyGlossaryPage';
import InvoicingStatusReportPage from './pages/reports/InvoicingStatusReportPage';
import FreelancerInvoiceReportPage from './pages/reports/FreelancerInvoiceReportPage';
import FreelancerInvoiceDetailPage from './pages/reports/FreelancerInvoiceDetailPage';
import WebAnalyticsPage from './pages/reports/WebAnalyticsPage';
import EngineeringMetricsPage from './pages/reports/EngineeringMetricsPage';
import EngineerDetailPage from './pages/reports/EngineerDetailPage';
import UnmappedCommitsPage from './pages/admin/UnmappedCommitsPage';
import CronJobsPage from './pages/admin/CronJobsPage';
// Phase 106 / D-02 — superadmin fleet console dashboard.
import FleetConsolePage from './pages/admin/FleetConsolePage';
import RespectAdminPage from './pages/admin/RespectAdminPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import DataImportPage from './pages/admin/DataImportPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import EmployeeTypePage from './pages/admin/EmployeeTypePage';
import TimeOffTypePage from './pages/admin/TimeOffTypePage';
import LabelManagementPage from './pages/admin/LabelManagementPage';
import ExtraCostPage from './pages/admin/ExtraCostPage';
import EmployeeExtraCostPage from './pages/admin/EmployeeExtraCostPage';
import BulkSalaryPage from './pages/admin/BulkSalaryPage';
import EmployeeExtraCostTypePage from './pages/admin/EmployeeExtraCostTypePage';
import EmployeeExtraCostDetailPage from './pages/admin/EmployeeExtraCostDetailPage';
import { AdminPendingTimesheetsPage } from './pages/admin/AdminPendingTimesheetsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ConnectPage } from './pages/bot/ConnectPage';
import { GiveKudosPage } from './pages/GiveKudosPage';
import TeamEventsPage from './pages/hr/TeamEventsPage';
import TeamEventDetailPage from './pages/hr/TeamEventDetailPage';
import OnboardingStepsPage from './pages/hr/OnboardingStepsPage';
import MerchSizesPage from './pages/hr/MerchSizesPage';
import EventTypePage from './pages/admin/EventTypePage';
import WebAnalyticsSitesAdminPage from './pages/admin/WebAnalyticsSitesAdminPage';
import UserRoleMatrixPage from './pages/admin/UserRoleMatrixPage';
import PermissionsPage from './pages/admin/PermissionsPage';
import AppAccessMatrixPage from './pages/admin/AppAccessMatrixPage';
import BotAdminPage from './pages/admin/BotAdminPage';
import RobotTasksPage from './pages/admin/RobotTasksPage';
import ExternalUsersPage from './pages/admin/ExternalUsersPage';
import PlanningMatrixPage from './pages/capacity/PlanningMatrixPage';
import TasksPage, { MyTasksRoute } from './pages/TasksPage';
import { ReportIssuePage } from './pages/ReportIssuePage';
// MKS-144 — full-page ticket view (permanent, shareable /tasks/:taskRef URL).
import { TaskDetailPage } from './pages/tasks/TaskDetailPage';
import { ManagerOverviewPage } from './pages/manager/ManagerOverviewPage';
import ProjectRolePage from './pages/admin/ProjectRolePage';
import PaymentTermsPage from './pages/admin/PaymentTermsPage';
import TaskLabelsAdminPage from './pages/admin/TaskLabelsAdminPage';
import LeadStagesPage from './pages/admin/LeadStagesPage';
import LabelProjectMappingsPage from './pages/admin/LabelProjectMappingsPage';
import ProjectTemplatesPage from './pages/admin/ProjectTemplatesPage';
import ProjectTemplateEditPage from './pages/admin/ProjectTemplateEditPage';
import LeadsPage from './pages/sales/LeadsPage';
// MKS-344 — reusable sales contacts.
import ContactsPage from './pages/sales/ContactsPage';
// Phase 107 (Plan 107-11) — SLA Pricing Configurator list + editor pages.
import SlaPricingListPage from './pages/sales/SlaPricingListPage';
import SlaPricingEditorPage from './pages/sales/SlaPricingEditorPage';
import { KanbanBoardPage } from './pages/projects/KanbanBoardPage';
// Phase 41 Plan 41-03 — new v7 routes (stubs land in Wave 1; real pages
// land in Wave 2 plans 41-05/06/09/11). Imports are added here so the
// Sidebar can link without producing 404s.
import { AdminTemplatesPage } from './pages/admin/AdminTemplatesPage';
import { SlaTiersAdminPage } from './pages/admin/SlaTiersAdminPage';
import { HelpdeskSettingsPage } from './pages/admin/HelpdeskSettingsPage';
import { PreferencesPage } from './pages/profile/PreferencesPage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
// Phase 46 (Plan 46-07 / SPEC Req 6) — admin notifications platform pages.
import { GlobalNotificationRoutesPage } from './pages/admin/GlobalNotificationRoutesPage';
import { NotificationsAuditPage } from './pages/admin/NotificationsAuditPage';
// Phase 48 (Plan 48-03) — admin orphan-role entries list.
import EntriesNeedingRolePage from './pages/admin/EntriesNeedingRolePage';
import TmConversionReadinessPage from './pages/admin/TmConversionReadinessPage';
// MKS-447 (part 6) — the Linear ↔ Monksheets reconciliation report.
import LinearReconciliationPage from './pages/admin/LinearReconciliationPage';
import LinearTeamRoutingPage from './pages/admin/LinearTeamRoutingPage';
// Phase 50 Plan 09 — SLA Breach Dashboard (manager + PM surface).
import { SlaBreachDashboardPage } from './pages/manager/SlaBreachDashboardPage';
import { ExternalAuthProvider } from './services/externalAuthContext';
import { ExternalRoute } from './components/auth/ExternalRoute';
import { ExternalLayout } from './components/layout/ExternalLayout';
import { LoginPage as ExternalLoginPage } from './pages/external/LoginPage';
import { ForgotPasswordPage as ExternalForgotPasswordPage } from './pages/external/ForgotPasswordPage';
import { ResetPasswordPage as ExternalResetPasswordPage } from './pages/external/ResetPasswordPage';
import { AcceptInvitePage as ExternalAcceptInvitePage } from './pages/external/AcceptInvitePage';
// Phase 57 Plan 57-01 — Customer Portal shell
import { PortalRequestsPage } from './pages/external/PortalRequestsPage';
import { ProjectsIndexRedirect } from './pages/external/ProjectsIndexRedirect';
// Phase 105 (Plan 105-04) — read-only external Leads list + detail.
import { LeadsPage as ExternalLeadsPage } from './pages/external/LeadsPage';
import { LeadDetailPage as ExternalLeadDetailPage } from './pages/external/LeadDetailPage';

// Placeholder pages - will be implemented in later phases
const UnauthorizedPage = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900">403</h1>
      <p className="mt-2 text-gray-500">You don&apos;t have permission to access this page</p>
      <a href="/dashboard" className="mt-4 inline-block text-primary-600 hover:underline">
        Go to dashboard
      </a>
    </div>
  </div>
);


function App() {
  // MKS-351: resolve the app-wide date format once, here, before any
  // descendant renders. Date formatting is called from ~50 plain functions
  // that cannot call a hook, so the flag is read in this one place and written
  // into the shared spec they all read. See hooks/useDateFormatSync.ts.
  useDateFormatSync();

  // MKS-314 follow-up: and the app-wide reporting-currency rates, for the same
  // reason and in the same pass — money is rendered from plain functions that
  // cannot call a hook. See hooks/useReportingFxSync.ts.
  useReportingFxSync();

  // MKS-503: Ctrl+Enter inside a textarea submits the form that owns it. One
  // `document` listener here reaches every route and every modal, portalled
  // ones included, so no form needs its own wiring. See
  // hooks/useCtrlEnterSubmit.ts.
  useCtrlEnterSubmit();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* External user surface — parallel auth context, no internal Layout chrome */}
      <Route
        path="/external/*"
        element={
          <ExternalAuthProvider>
            <Routes>
              {/* Public auth pages — no shell chrome */}
              <Route path="login" element={<ExternalLoginPage />} />
              <Route path="forgot-password" element={<ExternalForgotPasswordPage />} />
              <Route path="reset-password" element={<ExternalResetPasswordPage />} />
              <Route path="accept-invite" element={<ExternalAcceptInvitePage />} />

              {/* Authenticated external surface — wrapped in ExternalLayout (Phase 23 D-01).
                  B4: pathless layout route — NO path or path="*" attribute here. Public auth
                  routes above are siblings of this block, so they render WITHOUT ExternalLayout
                  chrome. The index redirect below is also a sibling. */}
              <Route
                element={
                  <ExternalRoute>
                    <ExternalLayout />
                  </ExternalRoute>
                }
              >
                {/* D-04: legacy /external/dashboard URL preserved as a redirect */}
                <Route path="dashboard" element={<Navigate to="/external/projects" replace />} />

                {/* Phase 23 NAV-02: landing surface. external-board-redirect-login
                    follow-up (2026-07-02) — instead of a static placeholder, land
                    the external user directly on the first project's board. Falls
                    back to the placeholder when they have no member projects. */}
                <Route path="projects" element={<ProjectsIndexRedirect />} />

                {/* Phase 24 D-13 — route path unchanged from Phase 23 (D-21 body-only swap). */}
                <Route path="projects/:id/board" element={<KanbanBoardPage audience="external" />} />

                {/* Phase 105 (D-01/D-02): read-only external Leads list + detail.
                    Deliberately NOT gated by the internal module-visibility wrapper used
                    elsewhere in this file: that wrapper reads useModules(), which depends on
                    an INTERNAL session and is unrelated to the external grant. Applying it
                    here would render NotFoundPage for every legitimately granted external
                    user. The real gate is `salesViewer` on GET /api/external/me (surfaced by
                    ExternalNavigation) plus the service-layer assertSalesViewerGrant on the
                    backend — see 105-04-PLAN.md <decisions_recorded>, "The external leads
                    route is NOT wrapped in" that module-visibility wrapper. */}
                <Route path="leads" element={<ExternalLeadsPage />} />
                <Route path="leads/:id" element={<ExternalLeadDetailPage />} />

                {/* Phase 57 Plan 57-01 — Customer Portal (PORTAL-01)
                    ExternalLayout conditionally mounts PortalTopNav instead of
                    ExternalNavigation for /external/portal/* routes. */}
                <Route path="portal" element={<Navigate to="projects" replace />} />
                <Route path="portal/projects/:projectId/requests" element={<PortalRequestsPage />} />
                <Route path="portal/projects/:projectId/requests/new" element={<PortalRequestsPage />} />
                <Route path="portal/projects/:projectId/requests/:taskId" element={<PortalRequestsPage />} />
                <Route
                  path="portal/projects/:projectId/requests/confirm/:taskId"
                  element={<PortalRequestsPage />}
                />
              </Route>

              <Route index element={<Navigate to="login" replace />} />
            </Routes>
          </ExternalAuthProvider>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/* Phase 41 Plan 41-03 (Open Q6): /dashboard mounts the v7 DashboardPage;
            the v6-era legacy mount was removed. Phase 89 Plan 89-03: the
            archived v6 dashboard's one-milestone-overlap route mount was
            removed after v7.0 closed. */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="helpdesk" element={<HelpdeskPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* MKS-508 — public company docs (docs/public), served by the backend at
            /api/company-docs. Internal users only: this block sits under the internal
            ProtectedRoute, never under /external/*. */}
        <Route path="docs" element={<DocsPage />} />
        <Route path="docs/:slug" element={<DocsPage />} />
        <Route path="give-kudos" element={<RequireModule module="people"><GiveKudosPage /></RequireModule>} />

        {/* Phase 73 (Plan 73-04) — nonce-gated Slack bot connect page (D-02/D-03/D-04/D-11).
            Nested under the parent `/` route above, which is already wrapped by
            <ProtectedRoute> — consistent with every other authenticated page here. */}
        <Route path="connect/:nonce" element={<RequireModule module="monka"><ConnectPage /></RequireModule>} />

        {/* Training compliance (mockup) — user-facing view, all authenticated users */}
        <Route path="trainings" element={<RequireModule module="isms"><MyTrainingsPage /></RequireModule>} />

        {/* Time Entry routes */}
        <Route path="time" element={<RequireModule module="time"><TimeEntryPage /></RequireModule>} />

        {/* Time Off routes */}
        <Route path="time-off" element={<RequireModule module="time"><TimeOffListPage /></RequireModule>} />
        <Route path="time-off/:id" element={<RequireModule module="time"><TimeOffDetailPage /></RequireModule>} />

        {/* Projects routes — list + create are admin-only; detail + edit
            are admin-or-PM (Phase 21.1 opened /edit, quick task 260512-dzo opened
            /:id so the in-page Capacity grid is reachable for the project's PM).
            Phase 104 removed the /:id/coverage editor route (D-01). */}
        {/* Persona UAT 2026-05-14 — PM sees the My Projects link in the
            Sidebar's Manage section but hit a 403 because the list route was
            admin-only. Backend `/api/projects` scopes the result per caller
            (PM → their own projects), so widening the FE gate to
            admin / manager / PM matches that scoping. */}
        <Route path="projects" element={<RequireModule module="projects"><AdminManagerOrPMRoute><ProjectListPage /></AdminManagerOrPMRoute></RequireModule>} />
        <Route path="projects/new" element={<RequireModule module="projects"><AdminRoute><ProjectFormPage /></AdminRoute></RequireModule>} />
        <Route path="projects/:id" element={<RequireModule module="projects"><AdminOrProjectPMRoute><ProjectDetailPage /></AdminOrProjectPMRoute></RequireModule>} />
        <Route path="projects/:id/edit" element={<RequireModule module="projects"><ProjectEditRoute><ProjectFormPage /></ProjectEditRoute></RequireModule>} />
        {/* Phase 24 D-13 — route path unchanged from Phase 23 (D-21 body-only swap). */}
        <Route path="projects/:id/board" element={<RequireModule module="workmgmt"><KanbanBoardPage audience="internal" /></RequireModule>} />

        {/* Employee Invoice routes (Freelance) */}
        <Route path="my-invoices" element={<RequireModule module="payroll"><EmployeeInvoiceListPage /></RequireModule>} />
        <Route path="my-invoices/:year/:month" element={<RequireModule module="payroll"><EmployeeInvoiceDetailPage /></RequireModule>} />

        {/* Quick task 260512-uat-batch (F-12a) — My Tasks personal-scope page. */}
        {/* MKS-477 — /my-tasks redirects to /tasks, keeping the query. */}
        <Route path="my-tasks" element={<RequireModule module="workmgmt"><MyTasksRoute /></RequireModule>} />

        {/* MKS-477 — Tasks page (My Tasks | All tasks tabs). The static `tasks` path and the
            `tasks/:taskRef` param route below never collide: only `/tasks` with no segment
            matches this one. */}
        <Route path="tasks" element={<RequireModule module="workmgmt"><TasksPage /></RequireModule>} />

        {/* MKS-144 — permanent, shareable full-page ticket view. No role
            guard: access is enforced server-side by
            requireProjectReadableByTask (GET /api/tasks/:taskId/locate);
            the page's own not-found state covers a denial. */}
        <Route path="tasks/:taskRef" element={<RequireModule module="workmgmt"><TaskDetailPage /></RequireModule>} />

        {/* 260727-gob — Report-an-issue page: lists open-board projects +
            the caller's own reports. Visible to every internal user (no
            role gate), same as My Tasks above. */}
        <Route path="report-issue" element={<RequireModule module="workmgmt"><ReportIssuePage /></RequireModule>} />

        {/* Expenses routes */}
        <Route path="expenses" element={<RequireModule module="billing"><ExpenseListPage /></RequireModule>} />
        <Route path="expenses/:id" element={<RequireModule module="billing"><ExpenseDetailPage /></RequireModule>} />

        {/* Invoices routes (admin + finance) */}
        <Route path="invoices" element={<RequireModule module="billing"><FinanceRoute><InvoiceListPage /></FinanceRoute></RequireModule>} />
        <Route path="invoices/new" element={<RequireModule module="billing"><FinanceRoute><InvoiceGeneratePage /></FinanceRoute></RequireModule>} />
        {/* Phase 62 Plan 07 — To Be Invoiced queue; placed BEFORE :id so static segment wins */}
        <Route path="invoices/pending" element={<RequireModule module="billing"><FinanceRoute><InvoicePendingPage /></FinanceRoute></RequireModule>} />
        {/* MKS-334 — Receivables: per-client overdue overview, aging columns and DSO.
            Also before :id, like its static siblings. */}
        <Route path="invoices/receivables" element={<RequireModule module="billing"><FinanceRoute><ReceivablesPage /></FinanceRoute></RequireModule>} />
        {/* MKS-349 — back-fill of issued invoice numbers from an accounting export. Also before :id. */}
        <Route path="invoices/import" element={<RequireModule module="billing"><FinanceRoute><InvoiceImportPage /></FinanceRoute></RequireModule>} />
        <Route path="invoices/:id" element={<RequireModule module="billing"><FinanceRoute><InvoiceDetailPage /></FinanceRoute></RequireModule>} />

        {/* Reports routes — Detailed Project Report accessible to managers (scoped); others admin-only */}
        {/* MKS-330: the landing page itself was <ProjectReportRoute> (admin /
            manager / PM), which bounced a finance-only user to /unauthorized
            before a single tile rendered — while the sidebar "Reports" link
            (components/ui/Sidebar.tsx) stayed visible to them. The gate is now
            the same superset the Project Report uses, so finance reaches the
            tiles ReportsPage.isTileVisible already shows it. */}
        <Route path="reports" element={<AdminFinanceManagerOrPMRoute><ReportsPage /></AdminFinanceManagerOrPMRoute>} />
        {/* Quick task 260428-lw0: Project Report summary opened to managers / PMs
            (scoped server-side via resolveAllowedProjectIds), mirroring the
            Detailed Project Report. Role set mirrors the backend gate
            (requireAdminFinanceManagerOrPM): admin + finance keep their Phase 27
            access; manager is added. PM-only personas (Project.projectManagerId
            but no role) are admitted server-side; the ReportsPage tile + sidebar
            already surface this to PMs, and the backend scopes their rows. */}
        <Route path="reports/project" element={<RequireModule module="analytics"><AdminFinanceManagerOrPMRoute><ProjectReportPage /></AdminFinanceManagerOrPMRoute></RequireModule>} />
        <Route path="reports/attendance" element={<RequireModule module="analytics"><ProtectedRoute requiredRoles={['admin', 'finance', 'hr']}><AttendanceReportPage /></ProtectedRoute></RequireModule>} />
        <Route path="reports/payroll" element={<RequireModule module="payroll"><FinanceRoute><PayrollReportPage /></FinanceRoute></RequireModule>} />
        <Route path="reports/billability" element={<RequireModule module="analytics"><FinanceRoute><BillabilityReportPage /></FinanceRoute></RequireModule>} />
        <Route path="reports/revenue" element={<RequireModule module="analytics"><FinanceRoute><RevenueKpiPage /></FinanceRoute></RequireModule>} />
        {/* MKS-330: both were stricter than the API they call. Detailed Project
            Report now mirrors GET /api/reports/project/detailed(+xlsx)
            (requireAdminFinanceManagerOrPM); Invoicing Status mirrors
            requireRoles('admin', 'finance') instead of being admin-only. */}
        <Route path="reports/project/detailed" element={<RequireModule module="analytics"><AdminFinanceManagerOrPMRoute><ProjectDetailedReportPage /></AdminFinanceManagerOrPMRoute></RequireModule>} />
        <Route path="reports/invoicing-status" element={<RequireModule module="analytics"><FinanceRoute><InvoicingStatusReportPage /></FinanceRoute></RequireModule>} />
        {/* MKS-332 — the money glossary. Same audience as /reports itself: if
            you can see a money figure you can look up what it means. The page
            hides the Cost and Margin cards from anyone who is not admin or
            finance, matching what the API sends them. */}
        <Route path="reports/money-glossary" element={<RequireModule module="analytics"><AdminFinanceManagerOrPMRoute><MoneyGlossaryPage /></AdminFinanceManagerOrPMRoute></RequireModule>} />
        <Route path="reports/freelancer-invoices" element={<RequireModule module="payroll"><FinanceRoute><FreelancerInvoiceReportPage /></FinanceRoute></RequireModule>} />
        <Route path="reports/freelancer-invoices/:userId/:year/:month" element={<RequireModule module="payroll"><FinanceRoute><FreelancerInvoiceDetailPage /></FinanceRoute></RequireModule>} />
        {/* Quick task 260512-sidebar-production-parity (2026-05-12) — Petra
            collapsed the sidebar Marketing section into a new "Business"
            section visible to (isSales || isAdmin). Web Analytics needs to
            stay reachable for marketing users too, and now needs to admit
            sales users clicking from the Business section. Route gating
            widened from MarketingRoute (admin+marketing) to admin +
            marketing + sales. */}
        <Route
          path="reports/web-analytics"
          element={
            <RequireModule module="analytics">
              <ProtectedRoute requiredRoles={['admin', 'marketing', 'sales']}>
                <WebAnalyticsPage />
              </ProtectedRoute>
            </RequireModule>
          }
        />

        {/* Phase 32 Plan 32-2 — engineering metrics (UI-SPEC §195-204). */}
        {/* Persona UAT 2026-05-14 — Engineering Metrics restricted to admin
            only (was ManagerRoute). PMs and managers should not see this until
            the product decides whether they should ingest engineering activity
            beyond their own projects. */}
        <Route path="reports/engineering" element={<RequireModule module="analytics"><AdminRoute><EngineeringMetricsPage /></AdminRoute></RequireModule>} />
        <Route path="reports/engineering/me" element={<RequireModule module="analytics"><ProtectedRoute><EngineerDetailPage /></ProtectedRoute></RequireModule>} />
        <Route path="reports/engineering/users/:userId" element={<RequireModule module="analytics"><ManagerRoute><EngineerDetailPage /></ManagerRoute></RequireModule>} />

        {/* Sales routes */}
        {/* MKS-347 — LeadsBoardRoute, not SalesRoute: behind the ticket's
            release flag it also admits marketing, whose API access to leads
            has always been read-only. The page renders read-only for them. */}
        <Route path="sales/leads" element={<RequireModule module="sales"><LeadsBoardRoute><LeadsPage /></LeadsBoardRoute></RequireModule>} />
        {/* MKS-344 — reusable sales contacts. */}
        <Route path="sales/contacts" element={<RequireModule module="sales"><SalesRoute><ContactsPage /></SalesRoute></RequireModule>} />
        {/* Phase 107 (Plan 107-11) / D-01 / D-11 — SLA Pricing Configurator.
            Needs neither a lead nor a project in context; gated sales+admin
            (SalesRoute), narrower than the marketing-inclusive Business
            section (D-03/SLA-PRICE-04 proof lives in
            App.slaPricingBoundary.test.tsx). Exactly one concrete
            configurator — no generalised parameterised route or registry
            of configurator variants exists here (D-02). */}
        <Route path="sales/sla-pricing" element={<RequireModule module="sales"><SalesRoute><SlaPricingListPage /></SalesRoute></RequireModule>} />
        <Route path="sales/sla-pricing/:id" element={<RequireModule module="sales"><SalesRoute><SlaPricingEditorPage /></SalesRoute></RequireModule>} />

        {/* HR routes (all authenticated users) */}
        <Route path="hr/team-events" element={<RequireModule module="people"><TeamEventsPage /></RequireModule>} />
        <Route path="hr/team-events/:id" element={<RequireModule module="people"><TeamEventDetailPage /></RequireModule>} />
        <Route path="hr/onboarding-steps" element={<RequireModule module="people"><HRRoute><OnboardingStepsPage /></HRRoute></RequireModule>} />
        {/* MKS-393 — the merch overview and its XLSX/CSV exports. */}
        <Route path="hr/merch-sizes" element={<RequireModule module="people"><HRRoute><MerchSizesPage /></HRRoute></RequireModule>} />

        {/* Manager routes */}
        {/* Phase 45 Plan 45-05 (D-09) — Manager Overview Variant B at /manager/overview.
            Gated by <ManagerRoute> (requiredRoles=['admin', 'manager'] per
            ProtectedRoute.tsx:40). Backend `requireManager` preHandler from
            45-02 is the defense-in-depth gate; the FE guard prevents
            render flash for non-managers who guess the URL. */}
        <Route
          path="manager/overview"
          element={
            <RequireModule module="workmgmt">
              <ManagerRoute>
                <ManagerOverviewPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        <Route
          path="manager/timesheets/pending"
          element={
            <RequireModule module="time">
              <ManagerRoute>
                <PendingTimesheetsPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        <Route
          path="manager/expenses/pending"
          element={
            <RequireModule module="billing">
              <ManagerRoute>
                <PendingExpensesPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        <Route
          path="manager/time-off/pending"
          element={
            <RequireModule module="time">
              <ManagerRoute>
                <PendingTimeOffsPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        <Route
          path="manager/timesheets/:id"
          element={
            <RequireModule module="time">
              <ManagerRoute>
                <ManagerTimesheetDetailPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        <Route
          path="manager/timesheets"
          element={
            <RequireModule module="time">
              <ManagerRoute>
                <PendingTimesheetsPage />
              </ManagerRoute>
            </RequireModule>
          }
        />
        {/* Guard mirrors Sidebar visibility (isAdmin || isPM) and backend
            requireManagerOrPM + service-scoped list: a PM lands on the page
            and sees only their own projects' requests (empty state if none),
            instead of a 403 /unauthorized. */}
        <Route path="admin/join-requests" element={<RequireModule module="projects"><AdminOrPMRoute><PendingJoinRequestsPage /></AdminOrPMRoute></RequireModule>} />
        {/* Phase 113 (MKS-208, D-10) — the central backdated-hours queue, the
            admin half of the approval surface. Same gates as its join-request
            sibling. */}
        <Route path="admin/backdated-hours-requests" element={<RequireModule module="projects"><AdminOrPMRoute><PendingBackdatedRequestsPage /></AdminOrPMRoute></RequireModule>} />
        <Route
          path="manager/tasks"
          element={
            <RequireModule module="workmgmt">
              <TaskManagerRoute>
                <TaskManagementPage />
              </TaskManagerRoute>
            </RequireModule>
          }
        />
        {/* Phase 50 Plan 09 — SLA Breach Dashboard (TIER-06: runtime breach snapshot
            grouped by tier × project; row click deep-links via ?openTask= per BD-01). */}
        <Route
          path="manager/sla-breach-dashboard"
          element={
            <RequireModule module="workmgmt">
              <ManagerRoute>
                <SlaBreachDashboardPage />
              </ManagerRoute>
            </RequireModule>
          }
        />

        {/* Admin routes */}
        <Route path="admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
        {/* Phase 99 (Plan 99-01) — CSV data import. No RequireModule wrapper:
            the page itself is core (it always offers at least the users
            importer) and per-entity module gating already happens server-side
            in `enabledCsvImportEntities()`. Do not "fix" this by adding a
            module wrapper — that would hide the page entirely on a
            projects-disabled instance. */}
        <Route path="admin/imports" element={<AdminRoute><DataImportPage /></AdminRoute>} />
        {/* Training compliance (mockup) — admin config: catalogue, requirements, matrix */}
        <Route path="admin/trainings" element={<RequireModule module="isms"><AdminRoute><TrainingAdminPage /></AdminRoute></RequireModule>} />
        <Route path="admin/users/:id" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
        <Route path="admin/employee-types" element={<RequireModule module="people"><AdminRoute><EmployeeTypePage /></AdminRoute></RequireModule>} />
        <Route path="admin/time-off-types" element={<RequireModule module="time"><AdminRoute><TimeOffTypePage /></AdminRoute></RequireModule>} />
        <Route path="admin/labels" element={<RequireModule module="projects"><AdminRoute><LabelManagementPage /></AdminRoute></RequireModule>} />
        <Route path="admin/extra-costs" element={<RequireModule module="billing"><FinanceRoute><ExtraCostPage /></FinanceRoute></RequireModule>} />
        <Route path="admin/employee-extra-cost-types" element={<RequireModule module="payroll"><FinanceRoute><EmployeeExtraCostTypePage /></FinanceRoute></RequireModule>} />
        <Route path="admin/event-types" element={<RequireModule module="people"><HRRoute><EventTypePage /></HRRoute></RequireModule>} />
        {/* Phase 85 Plan 85-06 (D-06) — registry write surface is admin-only even
            though the report itself is readable by admin + marketing + sales,
            because the registry determines what every reader's numbers are
            computed from. Do not widen this to AdminOrPMRoute or the report's
            three-role guard. */}
        <Route path="admin/web-analytics-sites" element={<RequireModule module="analytics"><AdminRoute><WebAnalyticsSitesAdminPage /></AdminRoute></RequireModule>} />
        <Route path="admin/project-roles" element={<RequireModule module="projects"><AdminRoute><ProjectRolePage /></AdminRoute></RequireModule>} />
        <Route path="admin/role-matrix" element={<RequireModule module="capacity"><AdminRoute><UserRoleMatrixPage /></AdminRoute></RequireModule>} />
        <Route path="admin/permissions" element={<AdminRoute><PermissionsPage /></AdminRoute>} />
        <Route path="admin/app-access" element={<RequireModule module="isms"><ISMSRoute><AppAccessMatrixPage /></ISMSRoute></RequireModule>} />
        {/* Phase 75 (Plan 75-05) — bot-admin page (D-07): connections table + kill switch */}
        <Route path="admin/bot" element={<RequireModule module="monka"><AdminRoute><BotAdminPage /></AdminRoute></RequireModule>} />
        {/* Phase 115 (Plan 115-06 / PCOPS-02) — the executor-aware robot-tasks
            dashboard, a standalone page (D-115-16), not a BotAdminPage tab. */}
        <Route path="admin/robot-tasks" element={<RequireModule module="monka"><AdminRoute><RobotTasksPage /></AdminRoute></RequireModule>} />
        <Route path="admin/external-users" element={<RequireModule module="portal"><AdminRoute><ExternalUsersPage /></AdminRoute></RequireModule>} />
        <Route path="admin/payment-terms" element={<RequireModule module="sales"><AdminRoute><PaymentTermsPage /></AdminRoute></RequireModule>} />
        {/* MKS-345 — the sales pipeline's kinds and win probabilities; the
            board header covers add/rename/recolour/reorder/delete. */}
        <Route path="admin/lead-stages" element={<RequireModule module="sales"><AdminRoute><LeadStagesPage /></AdminRoute></RequireModule>} />
        <Route path="admin/label-project-mappings" element={<RequireModule module="sales"><AdminRoute><LabelProjectMappingsPage /></AdminRoute></RequireModule>} />

        {/* Phase 46 (Plan 46-07) — admin notifications platform */}
        <Route path="admin/notifications/routes" element={<AdminRoute><GlobalNotificationRoutesPage /></AdminRoute>} />
        <Route path="admin/notifications-audit" element={<AdminRoute><NotificationsAuditPage /></AdminRoute>} />

        {/* Project templates (Phase 20 UI-04, D-21) */}
        <Route path="admin/project-templates" element={<RequireModule module="projects"><AdminRoute><ProjectTemplatesPage /></AdminRoute></RequireModule>} />
        <Route path="admin/project-templates/new" element={<RequireModule module="projects"><AdminRoute><ProjectTemplateEditPage /></AdminRoute></RequireModule>} />
        <Route path="admin/project-templates/:id" element={<RequireModule module="projects"><AdminRoute><ProjectTemplateEditPage /></AdminRoute></RequireModule>} />

        {/* Capacity planning */}
        {/* Quick task 260512-uat-batch (F-20) — Capacity Planning route widened
            from ManagerRoute to any authenticated user. Per-role data scope
            (admin: all, PM: PM-of projects + self, plain user: own row) is
            enforced server-side in capacityAssignmentService. */}
        <Route path="capacity/planning" element={<RequireModule module="capacity"><AdminOrPMRoute><PlanningMatrixPage /></AdminOrPMRoute></RequireModule>} />
        {/* quick-260813-hob — finance-only to match the route's
            requireFinanceOnly gate: admin alone does NOT qualify. Was
            HRRoute (Phase 84 / 84-05); moved off both HRRoute and
            FinanceRoute onto the strict FinanceOnlyRoute. */}
        <Route path="admin/monthly-salaries" element={<RequireModule module="payroll"><FinanceOnlyRoute><BulkSalaryPage /></FinanceOnlyRoute></RequireModule>} />
        <Route path="admin/employee-extra-costs" element={<RequireModule module="payroll"><FinanceRoute><EmployeeExtraCostPage /></FinanceRoute></RequireModule>} />
        <Route path="admin/employee-extra-costs/:id" element={<RequireModule module="payroll"><FinanceRoute><EmployeeExtraCostDetailPage /></FinanceRoute></RequireModule>} />
        <Route path="admin/timesheets/pending" element={<RequireModule module="time"><AdminRoute><AdminPendingTimesheetsPage /></AdminRoute></RequireModule>} />
        <Route path="admin/timesheets/:id" element={<RequireModule module="time"><AdminRoute><ManagerTimesheetDetailPage /></AdminRoute></RequireModule>} />
        {/* Phase 48 Plan 48-03 — TS-EDIT-04/05 admin orphan-role entries list */}
        <Route path="admin/entries-needing-role" element={<RequireModule module="time"><AdminRoute><EntriesNeedingRolePage /></AdminRoute></RequireModule>} />

        {/* MKS-372 — which Default-payment-type projects are ready to become
            Time & Material. The page itself is release-flag gated. */}
        <Route path="admin/tm-conversion-readiness" element={<RequireModule module="billing"><AdminRoute><TmConversionReadinessPage /></AdminRoute></RequireModule>} />

        {/* Phase 32 Plan 32-2 — admin unmapped commits surface (UI-SPEC §195-204). */}
        <Route path="admin/engineering/unmapped" element={<RequireModule module="analytics"><AdminRoute><UnmappedCommitsPage /></AdminRoute></RequireModule>} />

        {/* Phase 41 (UI consolidation) — v7 surfaces. Each route uses the
            appropriate role guard so Sidebar nav links never expose a
            non-permitted page. */}
        {/* Phase 104 — the project-coverage-editor route removed with the
            coverage editor UI (D-01 full retire). No redirect: no entry-CRUD
            page exists to redirect to (RESEARCH Assumption A3). */}
        <Route path="admin/templates" element={<RequireModule module="projects"><AdminRoute><AdminTemplatesPage /></AdminRoute></RequireModule>} />
        <Route path="admin/sla-tiers" element={<RequireModule module="workmgmt"><AdminRoute><SlaTiersAdminPage /></AdminRoute></RequireModule>} />
        {/* MKS-339 — helpdesk configuration (request categories, canned
            replies). RequireFlag is right here, unlike on /helpdesk itself:
            this page has no prior behaviour to keep, so a 404 while the flag
            is off IS the old behaviour. AdminOrPMRoute is a presentation gate;
            the backend decides per project and per reply. */}
        {/* MKS-406 — global task labels admin page */}
        <Route path="admin/task-labels" element={<RequireModule module="workmgmt"><AdminRoute><TaskLabelsAdminPage /></AdminRoute></RequireModule>} />
        <Route path="admin/helpdesk" element={<RequireModule module="workmgmt"><RequireFlag flag="mks_339_helpdesk_inbox"><AdminOrPMRoute><HelpdeskSettingsPage /></AdminOrPMRoute></RequireFlag></RequireModule>} />
        {/* MKS-447 (part 6) — the Linear mirror's reconciliation report. Same
            `workmgmt` module as the sync endpoint it reports on. */}
        <Route path="admin/linear-mirror" element={<RequireModule module="workmgmt"><AdminRoute><LinearReconciliationPage /></AdminRoute></RequireModule>} />
        {/* MKS-469 — the Linear team routing editor (team -> project, plus Linear
            label overrides). Behind the release flag that also gates label
            routing in the mirror itself. */}
        <Route path="admin/linear-team-routing" element={<RequireModule module="workmgmt"><AdminRoute><LinearTeamRoutingPage /></AdminRoute></RequireModule>} />
        <Route path="me/preferences" element={<ProtectedRoute><PreferencesPage /></ProtectedRoute>} />
        <Route path="notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

        {/* Phase 41 admin-cron-jobs branch (sibling Phase 41 numbering — kept
            historical prefix per phase_numbering_convention). D-12 / CRON-01..05. */}
        <Route path="admin/cron-jobs" element={<AdminRoute><CronJobsPage /></AdminRoute>} />
        {/* Phase 129 (D-16) — release flags are managed on the project's Engineering tab, so the
            admin page is gone (404). The path stays registered so a non-admin still lands on
            /unauthorized via AdminRoute rather than on the catch-all 404 below. */}
        <Route path="admin/release-flags" element={<AdminRoute><NotFoundPage /></AdminRoute>} />
        {/* Phase 106 / D-02 — superadmin fleet console. Deliberately NOT
            wrapped in RequireModule (P-27): the backing routes live in the
            always-on `core` module, and there is no `fleet` module. Gated
            by SuperadminRoute, not AdminRoute — a plain tenant admin must
            NOT reach this page. */}
        <Route path="admin/fleet" element={<SuperadminRoute><FleetConsolePage /></SuperadminRoute>} />
        {/* Phase 61 Plan 06 — Respect admin configuration (D-18/D-19/D-22/D-23). */}
        <Route path="admin/respect" element={<RequireModule module="people"><AdminRoute><RespectAdminPage /></AdminRoute></RequireModule>} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
